import { useEffect, useMemo, useRef, useState } from 'react';

import { Panel, PanelHeader } from './panel';
import { MiniSegmentedControl } from './mini-segmented-control';
import { NO_VALUE } from '@lib/page-utils/player-profile/format';

const MINIMUM_BAR_PERCENTAGE = 4;
const BAR_AREA_HEIGHT_PX = 130;

/**
 * Below this width a column's opponent label becomes unreadable, so the chart
 * scrolls horizontally rather than squeezing further.
 */
const MINIMUM_COLUMN_WIDTH_PX = 44;

/** Sentinel span meaning "every gameweek in the current filter window". */
const ALL_GAMEWEEKS = 0;

/** Gameweek spans the chart can be narrowed to, when enough data exists. */
const GAMEWEEK_SPAN_CHOICES = [5, 10, 19];

/**
 * A long window (a full past season is 38 gameweeks) opens on the latest ten
 * so the chart is readable at a glance; "All" stays one tap away.
 */
const DEFAULT_VISIBLE_GAMEWEEKS = 10;

export type FormChartPoint = {
  gameweek: number;
  opponentShortName: string | null;
  isHome: boolean | null;
  points: number | null;
  expectedPoints: number | null;
};

type FormChartProps = {
  points: FormChartPoint[];
  subtitle: string;
};

const toBarHeight = (value: number | null, scale: number): string => {
  if (value === null || scale <= 0) return '0%';
  return `${Math.max((value / scale) * 100, MINIMUM_BAR_PERCENTAGE)}%`;
};

const Legend = () => (
  <div className="flex items-center gap-4 text-xs text-neutral-700">
    <span className="flex items-center gap-1.5">
      <span aria-hidden className="h-2 w-2 rounded-sm bg-primary-600" />
      Points
    </span>
    <span className="flex items-center gap-1.5">
      <span aria-hidden className="h-2 w-2 rounded-sm bg-neutral-300" />
      Expected points
    </span>
  </div>
);

/**
 * Points against expected points, one paired column per gameweek. Both series
 * share a scale so the bars are directly comparable, and a gameweek the player
 * did not feature in renders as an empty column rather than a zero bar.
 *
 * Columns keep a readable minimum width, so a full season scrolls
 * horizontally (opening at the most recent gameweek) instead of compressing,
 * and the span control narrows the view to the latest few gameweeks.
 */
export const FormChart = ({ points, subtitle }: FormChartProps) => {
  // Null until the user picks a span, so the default can follow the data:
  // it resolves per render because `points` is empty until the fetch lands.
  const [chosenSpan, setChosenSpan] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const gameweekSpan =
    chosenSpan ??
    (points.length > DEFAULT_VISIBLE_GAMEWEEKS
      ? DEFAULT_VISIBLE_GAMEWEEKS
      : ALL_GAMEWEEKS);

  const spanOptions = useMemo(
    () => [
      ...GAMEWEEK_SPAN_CHOICES.filter((choice) => choice < points.length).map(
        (choice) => ({ value: choice, label: `Last ${choice}` })
      ),
      { value: ALL_GAMEWEEKS, label: 'All' }
    ],
    [points.length]
  );

  const visiblePoints =
    gameweekSpan === ALL_GAMEWEEKS ? points : points.slice(-gameweekSpan);

  // The most recent gameweeks are the interesting end, so land there whenever
  // the visible set changes rather than at gameweek one.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollLeft = container.scrollWidth;
  }, [visiblePoints.length, points]);

  const scale = Math.max(
    ...visiblePoints.flatMap((point) => [
      point.points ?? 0,
      point.expectedPoints ?? 0
    ]),
    1
  );

  const rowMinWidth = {
    minWidth: visiblePoints.length * MINIMUM_COLUMN_WIDTH_PX
  };

  return (
    <Panel>
      <PanelHeader
        title="Points vs expected points"
        subtitle={`${subtitle} · last ${visiblePoints.length} shown`}
        action={
          <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
            <Legend />
            {spanOptions.length > 1 && (
              <MiniSegmentedControl<number>
                ariaLabel="Gameweeks shown on the chart"
                options={spanOptions}
                value={gameweekSpan}
                onChange={setChosenSpan}
              />
            )}
          </div>
        }
      />
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto border-t border-neutral-200"
      >
        <div className="flex gap-2.5 px-5 pt-5 sm:px-6" style={rowMinWidth}>
          {visiblePoints.map((point) => {
            const hasPlayed = point.points !== null;
            return (
              <div
                key={point.gameweek}
                className="flex min-w-0 flex-1 flex-col items-center"
              >
                <span className="mb-1.5 text-[11px] font-bold tabular-nums text-neutral-800">
                  {hasPlayed ? point.points : NO_VALUE}
                </span>
                <div
                  className="flex w-full items-end justify-center gap-[3px]"
                  style={{ height: BAR_AREA_HEIGHT_PX }}
                >
                  <div
                    className="w-[45%] rounded-t-[3px] bg-primary-600"
                    style={{ height: toBarHeight(point.points, scale) }}
                  />
                  <div
                    className="w-[45%] rounded-t-[3px] bg-neutral-300"
                    style={{ height: toBarHeight(point.expectedPoints, scale) }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="flex gap-2.5 border-t border-neutral-200 px-5 pt-2.5 pb-5 sm:px-6"
          style={rowMinWidth}
        >
          {visiblePoints.map((point) => (
            <div
              key={point.gameweek}
              className="flex min-w-0 flex-1 flex-col items-center"
            >
              <span className="truncate text-[11px] font-semibold text-neutral-800">
                {point.opponentShortName ?? NO_VALUE}
                {point.isHome !== null && (
                  <span className="text-neutral-400">
                    {point.isHome ? ' (H)' : ' (A)'}
                  </span>
                )}
              </span>
              <span className="text-[10px] text-neutral-400">
                GW{point.gameweek}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};
